import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Archive, ArrowLeft, ChevronRight, ClipboardPlus, History, Pencil } from 'lucide-react';
import {
  fetchArbolFicha, fetchMencionesArbol, actualizarArbol, darDeBajaArbol, subirFotoArbol, crearEvaluacion,
  anularEvaluacion,
} from 'services/arbolesService';
import {
  estadoDesdeEvaluaciones, evaluacionesOrdenadas, mencionesDeArbol, historialFicha, cambiosArbol,
  idArbolValido, valorCampo, puedeEditarArboles, datosDeNotas, plazo, diaMes,
} from 'lib/arboles';
import { fechaCorta, fechaHoraCorta, nombreUsuario, plural } from 'lib/auditoria';
import { etiquetaInstalacion, etiquetasDe } from 'lib/auditoriaInstalaciones';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import { ESTADO_ICONO, ESTADO_LABEL } from 'lib/estadoUi';
import { hoyCR } from 'lib/fechaCR';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import Cargando from 'components/Cargando';
import useNombres from 'components/useNombres';
import { RiesgoDiscreto, MarcaVencida } from 'components/RiesgoPill';
import MotivoDialog from 'components/MotivoDialog';
import Button from 'components/ui/Button';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import FormArbol from './FormArbol';
import FormEvaluacion from './FormEvaluacion';
import { BotonFoto, DatosArbol, MenuMas, Seccion } from './FichaPartes';
import LineaEvaluaciones from './LineaEvaluaciones';
import { mensajeError } from 'lib/errores';

// Celda de la franja de resumen: etiqueta chica, valor destacado y una línea
// de apoyo atenuada.
function Celda({ label, children, apoyo }) {
  return (
    <div className="min-w-0 px-4 py-3 sm:px-5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold leading-tight text-foreground">{children}</div>
      {apoyo && <div className="mt-1 truncate text-xs text-muted-foreground">{apoyo}</div>}
    </div>
  );
}

// Estado de una mención en la inspección diaria: ícono de color y texto.
function EstadoMencion({ estado }) {
  const Icono = ESTADO_ICONO[estado];
  const color = estado === 'falla' ? 'text-error' : estado === 'alerta' ? 'text-warning' : 'text-success';
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
      {Icono && <Icono size={13} strokeWidth={2.5} className={color} aria-hidden="true" />}
      {ESTADO_LABEL[estado] || 'Sin dato'}
    </span>
  );
}

export default function ArbolFicha() {
  const { id } = useParams();
  const { role } = useAuth();
  const puedeEditar = puedeEditarArboles(role);
  const esAdmin = role === 'admin';
  const hoy = hoyCR();
  const [datos, setDatos] = useState({ cargando: true, error: null, arbol: null, evaluaciones: [], historial: [], errorHistorial: null });
  const [menciones, setMenciones] = useState({ cargando: true, error: null, filas: [] });
  const [editar, setEditar] = useState(false);
  const [evaluar, setEvaluar] = useState(false);
  const [dialogo, setDialogo] = useState(null); // { tipo: 'baja' } | { tipo: 'anular', evaluacion }
  const [foto, setFoto] = useState({ subiendo: false, error: '' });

  const cargar = useCallback(async () => {
    if (!idArbolValido(id)) {
      setDatos({ cargando: false, error: null, arbol: null, evaluaciones: [], historial: [], errorHistorial: null });
      return;
    }
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      const r = await fetchArbolFicha(id);
      setDatos({ cargando: false, error: null, ...r });
    } catch (e) {
      setDatos({ cargando: false, error: e, arbol: null, evaluaciones: [], historial: [], errorHistorial: null });
    }
  }, [id]);

  const cargarMenciones = useCallback(async () => {
    if (!idArbolValido(id)) return;
    setMenciones((m) => ({ ...m, cargando: true, error: null }));
    try {
      setMenciones({ cargando: false, error: null, filas: await fetchMencionesArbol(id) });
    } catch (e) {
      setMenciones({ cargando: false, error: e, filas: [] });
    }
  }, [id]);

  useEffect(() => { cargar(); cargarMenciones(); }, [cargar, cargarMenciones]);

  const { arbol, evaluaciones, historial } = datos;
  const estado = useMemo(() => estadoDesdeEvaluaciones(evaluaciones, hoy), [evaluaciones, hoy]);
  const evals = useMemo(() => evaluacionesOrdenadas(evaluaciones), [evaluaciones]);
  const cambios = useMemo(() => historialFicha(historial, arbol), [historial, arbol]);
  const lista = useMemo(() => mencionesDeArbol(menciones.filas, id), [menciones.filas, id]);
  const etiquetas = useMemo(() => etiquetasDe(arbol?.instalacion), [arbol]);
  const nombres = useNombres(useMemo(() => [
    arbol?.created_by, arbol?.updated_by, ...evaluaciones.flatMap((e) => [e.created_by, e.anulada_por]),
    ...cambios.map((c) => c.por),
  ], [arbol, evaluaciones, cambios]));

  const activo = arbol?.activo !== false;

  const guardarFicha = async (d) => {
    const c = cambiosArbol(arbol, d);
    if (!Object.keys(c).length) return;
    await actualizarArbol(arbol.id, c);
    await cargar();
  };

  const guardarEvaluacion = async (d, informe) => {
    const nueva = await crearEvaluacion(arbol.id, d, informe);
    setDatos((x) => ({ ...x, evaluaciones: [nueva, ...x.evaluaciones] }));
  };

  const cambiarFoto = async (archivo) => {
    setFoto({ subiendo: true, error: '' });
    try {
      await subirFotoArbol(arbol.id, archivo);
      setFoto({ subiendo: false, error: '' });
      await cargar();
    } catch (e) {
      setFoto({ subiendo: false, error: mensajeError(e, 'No se pudo subir la foto.') });
    }
  };

  const confirmarDialogo = async (motivo) => {
    if (dialogo.tipo === 'baja') {
      await darDeBajaArbol(arbol.id, motivo);
      await cargar();
    } else {
      const fila = await anularEvaluacion(dialogo.evaluacion.id, motivo);
      setDatos((x) => ({ ...x, evaluaciones: x.evaluaciones.map((e) => (e.id === fila.id ? { ...e, ...fila } : e)) }));
    }
  };

  const volver = (
    <Link to={arbol ? `/arboles${arbol.instalacion && arbol.instalacion !== 'canopy' ? `?instalacion=${arbol.instalacion}` : ''}` : '/arboles'}
      className="-mt-2 mb-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
      <ArrowLeft size={14} aria-hidden="true" /> Árboles
    </Link>
  );

  if (datos.cargando) return <AppShell titulo="Árbol">{volver}<Cargando texto="Cargando la ficha…" /></AppShell>;
  if (datos.error) {
    return (
      <AppShell titulo="Árbol">
        {volver}
        <ErrorState title="No se pudo cargar el árbol" message={mensajeError(datos.error)} onRetry={cargar} />
      </AppShell>
    );
  }
  if (!arbol) {
    return (
      <AppShell titulo="Árbol">
        {volver}
        <EmptyState framed title="Árbol no encontrado" description="El enlace no corresponde a ningún árbol registrado o su usuario no tiene acceso." />
      </AppShell>
    );
  }

  const especie = arbol.especie || datosDeNotas(arbol.notas).datos.nombre_cientifico || null;
  const nombre = arbol.nombre_comun || especie || 'Especie sin registrar';
  const opcionesMas = [
    ...(puedeEditar && activo ? [{ id: 'editar', label: 'Editar ficha', icono: Pencil, onClick: () => setEditar(true) }] : []),
    ...(esAdmin && activo ? [{ id: 'baja', label: 'Dar de baja', icono: Archive, peligro: true, onClick: () => setDialogo({ tipo: 'baja' }) }] : []),
  ];
  const ultimaMencion = lista[0];

  return (
    <AppShell>
      {volver}

      {/* Encabezado: código grande, nombres y estado; acciones compactas. */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <h1 className="font-heading text-5xl font-bold leading-none tracking-tight text-foreground tabular-nums sm:text-6xl">
            <span className="sr-only">Árbol </span>{arbol.codigo}
          </h1>
          <div className="min-w-0 pt-0.5">
            <p className="text-xl font-semibold leading-tight text-foreground">{nombre}</p>
            {arbol.nombre_comun && especie && <p className="mt-0.5 text-sm italic text-muted-foreground">{especie}</p>}
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              {arbol.sostiene && <span>{arbol.sostiene}</span>}
              <span className={arbol.sostiene ? 'border-l border-border pl-3' : ''}>{etiquetaInstalacion(arbol.instalacion)}</span>
              {activo && <span className="border-l border-border pl-3"><RiesgoDiscreto riesgo={estado.riesgo} /></span>}
            </p>
          </div>
        </div>
        {activo && (puedeEditar || opcionesMas.length > 0) && (
          <div className="flex shrink-0 items-center gap-2">
            {puedeEditar && (
              <Button size="sm" className="flex-1 sm:flex-none" onClick={() => setEvaluar(true)}>
                <ClipboardPlus size={15} className="mr-1.5" aria-hidden="true" />Nueva evaluación
              </Button>
            )}
            <MenuMas opciones={opcionesMas} />
          </div>
        )}
      </header>

      <div className="mt-6 space-y-5">
        {!activo && (
          <p className="rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground" data-testid="arbol-baja">
            Dado de baja{arbol.baja_at ? ` el ${fechaHoraCorta(arbol.baja_at)}` : ''}: {arbol.baja_motivo || 'sin motivo'}. La ficha y sus evaluaciones se conservan.
          </p>
        )}

        {/* Resumen en una franja: riesgo, última, próxima e inspección diaria. */}
        <section aria-label="Resumen del árbol" data-testid="estado-arbol"
          className="grid grid-cols-2 overflow-hidden rounded-xl border border-border bg-card lg:grid-cols-4 [&>*]:border-border max-lg:[&>*:nth-child(odd)]:border-r max-lg:[&>*:nth-child(-n+2)]:border-b lg:[&>*:not(:last-child)]:border-r">
          <Celda label="Riesgo actual"
            apoyo={estado.riesgo_fecha ? `Según la evaluación del ${diaMes(estado.riesgo_fecha)} ${String(estado.riesgo_fecha).slice(0, 4)}` : (estado.ultima ? 'Ninguna evaluación lo clasificó' : 'Sin evaluaciones')}>
            <RiesgoDiscreto riesgo={estado.riesgo} grande />
          </Celda>
          <Celda label="Última evaluación" apoyo={estado.ultima ? estado.ultima.evaluador_nombre || 'Sin nombre' : null}>
            {estado.ultima ? fechaCorta(estado.ultima.fecha) : <span className="font-normal text-muted-foreground">Ninguna</span>}
          </Celda>
          <Celda label="Próxima evaluación"
            apoyo={estado.vencida ? <MarcaVencida /> : estado.proxima_evaluacion ? plazo(hoy, estado.proxima_evaluacion) : null}>
            {estado.proxima_evaluacion ? fechaCorta(estado.proxima_evaluacion) : <span className="font-normal text-muted-foreground">Sin definir</span>}
          </Celda>
          <Celda label="Inspección diaria"
            apoyo={ultimaMencion ? `La última el ${diaMes(ultimaMencion.fecha)} ${String(ultimaMencion.fecha).slice(0, 4)}` : menciones.cargando || menciones.error ? null : 'Sin alertas ni fallas'}>
            {menciones.cargando ? <span className="font-normal text-muted-foreground">Buscando…</span>
              : menciones.error ? <span className="font-normal text-muted-foreground">Sin datos</span>
                : lista.length ? <a href="#t-menciones" className="hover:underline">{plural(lista.length, 'mención', 'menciones')}</a>
                  : <span className="font-normal text-muted-foreground">Ninguna</span>}
          </Celda>
        </section>

        <Seccion id="t-datos" titulo="Datos del árbol"
          acciones={puedeEditar && activo && <BotonFoto tieneFoto={!!arbol.foto_path} onElegir={cambiarFoto} subiendo={foto.subiendo} />}>
          <DatosArbol arbol={arbol} nombres={nombres} errorFoto={foto.error} />
        </Seccion>

        <Seccion id="t-evaluaciones" titulo="Evaluaciones" detalle={evals.length || null}>
          {evals.length === 0 ? (
            <EmptyState size="sm" title="Sin evaluaciones registradas."
              description={puedeEditar && activo ? 'Use "Nueva evaluación" para registrar la del arborista.' : undefined} />
          ) : (
            <LineaEvaluaciones evaluaciones={evals} hoy={hoy} esAdmin={esAdmin} nombres={nombres}
              onAnular={(x) => setDialogo({ tipo: 'anular', evaluacion: x })} />
          )}
        </Seccion>

        <Seccion id="t-menciones" titulo="En la inspección diaria" detalle={lista.length || null}>
          {menciones.error ? (
            <ErrorState size="sm" title="No se pudieron cargar las menciones" message={mensajeError(menciones.error)} onRetry={cargarMenciones} />
          ) : menciones.cargando ? (
            <Cargando className="py-6" texto="Buscando menciones…" />
          ) : lista.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ninguna inspección diaria ha marcado este árbol en alerta o falla.</p>
          ) : (
            <>
              <ul className="-my-2 divide-y divide-border/70">
                {lista.map((m) => (
                  <li key={`${m.inspeccion_id}:${m.itemKey}`} data-testid="mencion"
                    className="grid gap-x-4 gap-y-1 py-3 sm:grid-cols-[8.5rem_5.5rem_minmax(0,1fr)_auto] sm:items-baseline">
                    <span className="text-sm font-semibold tabular-nums text-foreground">{fechaCorta(m.fecha)}</span>
                    <span>{m.estado && <EstadoMencion estado={m.estado} />}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{etiquetas[m.itemKey] || m.itemKey}</p>
                      {m.nota && <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{m.nota}</p>}
                      <p className="mt-0.5 text-xs text-muted-foreground">{m.guia || 'Sin nombre'}</p>
                    </div>
                    <Link to={rutaInspeccion(m.inspeccion_id)} className="inline-flex items-center gap-0.5 text-sm font-medium text-primary underline-offset-2 hover:underline">
                      Ver inspección <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">{plural(lista.length, 'mención', 'menciones')} en inspecciones vigentes.</p>
            </>
          )}
        </Seccion>

        <Seccion id="t-historial" titulo="Historial de cambios" detalle={cambios.length || null}>
          {datos.errorHistorial ? (
            <p className="text-sm text-muted-foreground">{mensajeError(datos.errorHistorial)}</p>
          ) : cambios.length === 0 ? (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><History size={14} aria-hidden="true" />La ficha no se ha modificado desde que se registró.</p>
          ) : (
            <ol className="-my-2 divide-y divide-border/70">
              {cambios.map((c) => (
                <li key={c.id} data-testid="cambio" className="grid gap-x-4 gap-y-1.5 py-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{c.at ? fechaHoraCorta(c.at) : 'Sin fecha'}</p>
                    <p className="text-xs text-muted-foreground">{c.por ? nombreUsuario(c.por, nombres) : 'Sin dato'}</p>
                  </div>
                  {c.cambios.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin cambios visibles en la ficha.</p>
                  ) : (
                    <dl className="space-y-1.5">
                      {c.cambios.map((x) => (
                        <div key={x.campo} className="grid gap-x-3 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
                          <dt className="text-xs text-muted-foreground sm:pt-0.5">{x.label}</dt>
                          <dd className="min-w-0 break-words text-foreground">
                            <span className="text-muted-foreground line-through">{valorCampo(x.campo, x.antes)}</span>
                            <span className="mx-1.5 text-muted-foreground" aria-hidden="true">→</span>
                            <span className="sr-only"> cambió a </span>
                            {valorCampo(x.campo, x.despues)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Seccion>
      </div>

      <FormArbol open={editar} onClose={() => setEditar(false)} arbol={arbol} onGuardar={guardarFicha} />
      <FormEvaluacion open={evaluar} onClose={() => setEvaluar(false)} arbol={arbol} onGuardar={guardarEvaluacion} />
      <MotivoDialog open={!!dialogo} onClose={() => setDialogo(null)} peligro
        titulo={dialogo?.tipo === 'baja' ? `Dar de baja el árbol ${arbol.codigo}` : 'Anular evaluación'}
        confirmar={dialogo?.tipo === 'baja' ? 'Dar de baja' : 'Anular evaluación'}
        etiqueta={dialogo?.tipo === 'baja' ? 'Motivo de la baja' : 'Motivo de la anulación'}
        descripcion={dialogo?.tipo === 'baja'
          ? 'El árbol deja de aparecer en la lista y en la inspección diaria. La ficha, sus evaluaciones y su historial se conservan.'
          : dialogo && <>La evaluación del {fechaCorta(dialogo.evaluacion.fecha)} deja de contar para el riesgo. Queda guardada con su motivo.</>}
        onConfirm={confirmarDialogo} />
    </AppShell>
  );
}
